import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Star, Trash2, MessageCircle, MapPin, Search, CloudSun, AlertTriangle, Clock, Waves, Wind, Smile } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/PageShell';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../utils/api';


export default function Sessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allSpots, setAllSpots] = useState([]);
  const [spotQuery, setSpotQuery] = useState('');
  const [showSpotDropdown, setShowSpotDropdown] = useState(false);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [autoConditions, setAutoConditions] = useState(null);
  const [userAdjusted, setUserAdjusted] = useState(false);
  const [myBoards, setMyBoards] = useState([]);
  const [boardQuery, setBoardQuery] = useState('');
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);
  const [isRental, setIsRental] = useState(false);
  const spotRef = useRef(null);
  const boardRef = useRef(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    location: '',
    duration_minutes: '',
    wave_height: '',
    board: '',
    board_id: '',
    is_rental: false,
    conditions: '',
    notes: '',
    rating: 3,
  });

  useEffect(() => { loadSessions(); loadSpots(); loadBoards(); }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (spotRef.current && !spotRef.current.contains(e.target)) setShowSpotDropdown(false);
      if (boardRef.current && !boardRef.current.contains(e.target)) setShowBoardDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadSpots = async () => {
    try {
      const res = await authFetch(`${API}/api/spots`);
      if (res.ok) {
        const data = await res.json();
        setAllSpots(data.spots || []);
      }
    } catch {}
  };

  const loadBoards = async () => {
    try {
      const res = await authFetch(`${API}/api/boards`);
      if (res.ok) setMyBoards((await res.json()).boards || []);
    } catch {}
  };

  const fetchConditionsForSpot = async (spotName) => {
    setLoadingConditions(true);
    setUserAdjusted(false);
    try {
      const res = await authFetch(`${API}/api/spot-conditions?spot=${encodeURIComponent(spotName)}`);
      if (res.ok) {
        const data = await res.json();
        if (!data.error) {
          setAutoConditions(data);
          const waveStr = data.wave_height_ft ? `${data.wave_height_ft}ft` : '';
          const condStr = [
            data.summary || '',
            data.wind_speed_kph ? `${data.wind_direction} wind ${data.wind_speed_kph}kph` : '',
            data.swell_height_ft ? `${data.swell_height_ft}ft swell @ ${data.swell_period_s}s` : '',
            data.air_temp_f ? `${data.air_temp_f}°F` : '',
            data.uv_index ? `UV ${data.uv_index}` : '',
          ].filter(Boolean).join(' · ');
          setForm(prev => ({ ...prev, wave_height: waveStr, conditions: condStr }));
        }
      }
    } catch {} finally { setLoadingConditions(false); }
  };

  const loadSessions = async () => {
    try {
      const res = await authFetch(`${API}/api/sessions`);
      if (res.ok) setSessions((await res.json()).sessions || []);
    } catch {} finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowForm(false);
        setSpotQuery('');
        setBoardQuery('');
        setIsRental(false);
        setAutoConditions(null);
        setUserAdjusted(false);
        setForm({ date: new Date().toISOString().split('T')[0], location: '', duration_minutes: '', wave_height: '', board: '', board_id: '', is_rental: false, conditions: '', notes: '', rating: 3 });
        loadSessions();
      }
    } catch {}
  };

  const handleDelete = async (sessionId) => {
    try {
      await authFetch(`${API}/api/sessions/${sessionId}`, { method: 'DELETE',  });
      loadSessions();
    } catch {}
  };

  if (loading) {
    return (
      <PageShell title="Sessions">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Sessions">
      <div className="p-5" data-testid="sessions-page">
        {/* Add Button */}
        <div className="flex justify-end mb-4">
          <button
            data-testid="new-session-btn"
            onClick={() => setShowForm(!showForm)}
            className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors"
          >
            {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {showForm ? 'Cancel' : 'Log Session'}
          </button>
        </div>

        {/* Form */}
        <AnimatePresence>
          {showForm && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-5"
              onSubmit={handleSubmit}
              data-testid="session-form"
            >
              <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-4 space-y-3">
                {/* Date + Spot row */}
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="session-date-input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50" required />
                  {/* Spot search/select */}
                  <div className="relative" ref={spotRef}>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/50 pointer-events-none" />
                      <input
                        data-testid="session-location-input"
                        type="text"
                        value={spotQuery || form.location}
                        onChange={e => {
                          setSpotQuery(e.target.value);
                          setForm({...form, location: e.target.value});
                          setShowSpotDropdown(true);
                        }}
                        onFocus={() => setShowSpotDropdown(true)}
                        placeholder="search spots"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-7 pr-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
                        required
                        autoComplete="off"
                      />
                    </div>
                    {showSpotDropdown && (spotQuery || '').length > 0 && (() => {
                      const q = (spotQuery || '').toLowerCase();
                      const filtered = allSpots.filter(s => s.name.toLowerCase().includes(q)).slice(0, 6);
                      const exactMatch = allSpots.some(s => s.name.toLowerCase() === q);
                      if (filtered.length === 0 && !q) return null;
                      return (
                        <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-bg-paper border border-white/10 rounded-xl shadow-xl overflow-hidden max-h-[180px] overflow-y-auto">
                          {filtered.map(spot => (
                            <button
                              key={spot.name}
                              type="button"
                              data-testid={`spot-option-${spot.name}`}
                              onClick={() => {
                                setForm({...form, location: spot.name});
                                setSpotQuery(spot.name);
                                setShowSpotDropdown(false);
                                fetchConditionsForSpot(spot.name);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
                            >
                              <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs truncate">{spot.name}</span>
                              <span className="text-[9px] text-muted-foreground/50 ml-auto flex-shrink-0">{spot.region || ''}</span>
                            </button>
                          ))}
                          {!exactMatch && q.length > 1 && (
                            <button
                              type="button"
                              data-testid="spot-add-new"
                              onClick={() => {
                                setForm({...form, location: spotQuery});
                                setShowSpotDropdown(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-primary/10 transition-colors border-t border-white/5"
                            >
                              <Plus className="w-3 h-3 text-primary flex-shrink-0" />
                              <span className="text-xs text-primary font-medium">"{spotQuery}"</span>
                              <span className="text-[9px] text-muted-foreground/50 ml-auto flex-shrink-0">new spot</span>
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Hidden data inputs for form state */}
                <input data-testid="session-duration-input" type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: e.target.value === '' ? '' : (parseInt(e.target.value) || 0)})} className="hidden" />
                <input data-testid="session-wave-height-input" type="text" value={form.wave_height} onChange={e => { setForm({...form, wave_height: e.target.value}); if (autoConditions) setUserAdjusted(true); }} className="hidden" />
                <input data-testid="session-conditions-input" type="text" value={form.conditions} onChange={e => { setForm({...form, conditions: e.target.value}); if (autoConditions) setUserAdjusted(true); }} className="hidden" />

                {/* Board selector */}
                <div className="relative" ref={boardRef}>
                    <div className="relative">
                      <input
                        data-testid="session-board-input"
                        type="text"
                        value={boardQuery || form.board}
                        onChange={e => {
                          setBoardQuery(e.target.value);
                          setForm({...form, board: e.target.value, board_id: '', is_rental: false});
                          setIsRental(false);
                          setShowBoardDropdown(true);
                        }}
                        onFocus={() => setShowBoardDropdown(true)}
                        placeholder="board"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
                        autoComplete="off"
                      />
                    </div>
                    {showBoardDropdown && (() => {
                      const q = (boardQuery || '').toLowerCase();
                      const filtered = myBoards.filter(b => !q || b.name.toLowerCase().includes(q));
                      return (
                        <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-bg-paper border border-white/10 rounded-xl shadow-xl overflow-hidden max-h-[180px] overflow-y-auto">
                          {filtered.map(b => (
                            <button
                              key={b.board_id}
                              type="button"
                              data-testid={`board-option-${b.board_id}`}
                              onClick={() => {
                                const label = [b.name, b.length, b.board_type].filter(Boolean).join(' · ');
                                setForm({...form, board: label, board_id: b.board_id, is_rental: false});
                                setBoardQuery(label);
                                setIsRental(false);
                                setShowBoardDropdown(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
                            >
                              <span className="text-sm">🏄</span>
                              <div className="flex-1 min-w-0">
                                <span className="text-xs truncate block">{b.name}</span>
                                <span className="text-[9px] text-muted-foreground/50">{[b.board_type, b.length].filter(Boolean).join(' · ')}</span>
                              </div>
                              <span className="text-[9px] text-muted-foreground/40 flex-shrink-0">{b.session_count || 0} sessions</span>
                            </button>
                          ))}
                          <button
                            type="button"
                            data-testid="board-rental-option"
                            onClick={() => {
                              const rentalName = boardQuery || 'Rental board';
                              setForm({...form, board: rentalName, board_id: '', is_rental: true});
                              setBoardQuery(rentalName);
                              setIsRental(true);
                              setShowBoardDropdown(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-yellow-400/10 transition-colors border-t border-white/5"
                          >
                            <span className="text-[10px]">🤙</span>
                            <span className="text-xs text-yellow-400 font-medium">Rental board</span>
                            <span className="text-[9px] text-muted-foreground/50 ml-auto">not in my garage</span>
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                {/* Duration selector */}
                <div data-testid="duration-selector">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Clock className="w-3 h-3 text-muted-foreground/50" />
                    <span className="text-[10px] text-muted-foreground/60 font-medium">Duration</span>
                    {form.duration_minutes > 0 && <span className="text-[10px] text-primary font-bold ml-auto">{form.duration_minutes} min</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: '30m', value: 30 },
                      { label: '45m', value: 45 },
                      { label: '1hr', value: 60 },
                      { label: '1.5hr', value: 90 },
                      { label: '2hr', value: 120 },
                      { label: '2.5hr', value: 150 },
                      { label: '3hr', value: 180 },
                      { label: '3hr+', value: 210 },
                    ].map(d => (
                      <button
                        key={d.value}
                        type="button"
                        data-testid={`duration-${d.value}`}
                        onClick={() => setForm({...form, duration_minutes: form.duration_minutes === d.value ? '' : d.value})}
                        className={`text-[10px] px-2.5 py-1.5 rounded-lg font-medium transition-all ${
                          form.duration_minutes === d.value
                            ? 'bg-primary/15 text-primary border border-primary/30'
                            : 'bg-white/[0.04] text-muted-foreground/60 border border-white/5 hover:border-white/15'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wave height selector */}
                <div data-testid="wave-height-selector">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Waves className="w-3 h-3 text-muted-foreground/50" />
                    <span className="text-[10px] text-muted-foreground/60 font-medium">Wave Height</span>
                    {form.wave_height && <span className="text-[10px] text-cyan-400 font-bold ml-auto">{form.wave_height}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: 'Flat', value: 'Flat (0-1ft)' },
                      { label: 'Knee', value: '1-2ft' },
                      { label: 'Waist', value: '2-3ft' },
                      { label: 'Chest', value: '3-4ft' },
                      { label: 'Head', value: '4-5ft' },
                      { label: 'Overhead', value: '5-7ft' },
                      { label: 'DOH', value: '7-10ft' },
                      { label: 'XXL', value: '10ft+' },
                    ].map(w => (
                      <button
                        key={w.value}
                        type="button"
                        data-testid={`wave-${w.label.toLowerCase()}`}
                        onClick={() => {
                          setForm({...form, wave_height: form.wave_height === w.value ? '' : w.value});
                          if (autoConditions) setUserAdjusted(true);
                        }}
                        className={`text-[10px] px-2.5 py-1.5 rounded-lg font-medium transition-all ${
                          form.wave_height === w.value
                            ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                            : 'bg-white/[0.04] text-muted-foreground/60 border border-white/5 hover:border-white/15'
                        }`}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Conditions selector */}
                <div data-testid="conditions-selector">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Wind className="w-3 h-3 text-muted-foreground/50" />
                    <span className="text-[10px] text-muted-foreground/60 font-medium">Conditions</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: 'Glassy', emoji: '' },
                      { label: 'Clean', emoji: '' },
                      { label: 'Offshore', emoji: '' },
                      { label: 'Onshore', emoji: '' },
                      { label: 'Cross-shore', emoji: '' },
                      { label: 'Choppy', emoji: '' },
                      { label: 'Mushy', emoji: '' },
                      { label: 'Hollow', emoji: '' },
                      { label: 'Stormy', emoji: '' },
                      { label: 'Crowded', emoji: '' },
                    ].map(c => {
                      const selected = (form.conditions || '').includes(c.label);
                      return (
                        <button
                          key={c.label}
                          type="button"
                          data-testid={`cond-${c.label.toLowerCase()}`}
                          onClick={() => {
                            const current = (form.conditions || '').split(' · ').filter(Boolean);
                            let next;
                            if (selected) {
                              next = current.filter(x => x !== c.label);
                            } else {
                              next = [...current, c.label];
                            }
                            setForm({...form, conditions: next.join(' · ')});
                            if (autoConditions) setUserAdjusted(true);
                          }}
                          className={`text-[10px] px-2.5 py-1.5 rounded-lg font-medium transition-all ${
                            selected
                              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                              : 'bg-white/[0.04] text-muted-foreground/60 border border-white/5 hover:border-white/15'
                          }`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                  {autoConditions && form.conditions && (
                    <p className="text-[8px] text-muted-foreground/30 mt-1.5">
                      {userAdjusted ? 'Adjusted from live data' : `Auto-filled from ${autoConditions.spot}`}
                    </p>
                  )}
                </div>

                {/* Board + Conditions tags */}
                {isRental && (
                  <div className="flex items-center gap-2 text-[10px] rounded-lg px-2.5 py-1.5 bg-yellow-400/10 text-yellow-400" data-testid="rental-tag">
                    <span>🤙</span><span className="font-semibold">Rental Board</span><span className="text-[9px] opacity-70 ml-1">— not from your garage</span>
                  </div>
                )}
                {form.board_id && !isRental && (
                  <div className="flex items-center gap-2 text-[10px] rounded-lg px-2.5 py-1.5 bg-primary/10 text-primary" data-testid="own-board-tag">
                    <span>🏄</span><span className="font-semibold">From Your Garage</span>
                  </div>
                )}

                {/* Conditions status */}
                {loadingConditions && (
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <CloudSun className="w-3 h-3 animate-pulse" />
                    <span>fetching live conditions...</span>
                  </div>
                )}
                {autoConditions && !loadingConditions && (
                  <div className={`flex items-center gap-2 text-[10px] rounded-lg px-2.5 py-1.5 ${userAdjusted ? 'bg-yellow-400/10 text-yellow-400' : 'bg-green-400/10 text-green-400'}`} data-testid="conditions-tag">
                    {userAdjusted ? (
                      <><AlertTriangle className="w-3 h-3 flex-shrink-0" /><span className="font-semibold">User Adjusted Conditions</span><span className="text-[9px] opacity-70 ml-1">— auto-filled from live data, then edited</span></>
                    ) : (
                      <><CloudSun className="w-3 h-3 flex-shrink-0" /><span className="font-semibold">Live Conditions</span><span className="text-[9px] opacity-70 ml-1">— auto-filled from {autoConditions.spot}</span></>
                    )}
                  </div>
                )}
                {/* How did it feel — quick moods */}
                <div data-testid="mood-selector">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Smile className="w-3 h-3 text-muted-foreground/50" />
                    <span className="text-[10px] text-muted-foreground/60 font-medium">How did it feel?</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {[
                      { label: 'Stoked!', color: 'amber' },
                      { label: 'Epic', color: 'violet' },
                      { label: 'Fun', color: 'green' },
                      { label: 'Mellow', color: 'blue' },
                      { label: 'Challenging', color: 'orange' },
                      { label: 'Frustrating', color: 'red' },
                      { label: 'Exhausting', color: 'pink' },
                      { label: 'Progressed', color: 'cyan' },
                      { label: 'Zen', color: 'teal' },
                    ].map(m => {
                      const selected = (form.notes || '').includes(m.label);
                      const colorMap = {
                        amber: selected ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : '',
                        violet: selected ? 'bg-violet-500/15 text-violet-400 border-violet-500/30' : '',
                        green: selected ? 'bg-green-500/15 text-green-400 border-green-500/30' : '',
                        blue: selected ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : '',
                        orange: selected ? 'bg-orange-500/15 text-orange-400 border-orange-500/30' : '',
                        red: selected ? 'bg-red-500/15 text-red-400 border-red-500/30' : '',
                        pink: selected ? 'bg-pink-500/15 text-pink-400 border-pink-500/30' : '',
                        cyan: selected ? 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' : '',
                        teal: selected ? 'bg-teal-500/15 text-teal-400 border-teal-500/30' : '',
                      };
                      return (
                        <button
                          key={m.label}
                          type="button"
                          data-testid={`mood-${m.label.toLowerCase().replace('!', '')}`}
                          onClick={() => {
                            // Parse existing notes: mood tags are at the start, free text after " — "
                            const parts = (form.notes || '').split(' — ');
                            const moodPart = parts[0] || '';
                            const textPart = parts.slice(1).join(' — ');
                            const moods = moodPart.split(' + ').filter(x => x.trim() && ['Stoked!','Epic','Fun','Mellow','Challenging','Frustrating','Exhausting','Progressed','Zen'].includes(x.trim()));
                            let nextMoods;
                            if (selected) {
                              nextMoods = moods.filter(x => x !== m.label);
                            } else {
                              nextMoods = [...moods, m.label];
                            }
                            const moodStr = nextMoods.join(' + ');
                            const newNotes = textPart ? `${moodStr} — ${textPart}` : moodStr;
                            setForm({...form, notes: newNotes});
                          }}
                          className={`text-[10px] px-2.5 py-1.5 rounded-lg font-medium transition-all border ${
                            selected
                              ? colorMap[m.color]
                              : 'bg-white/[0.04] text-muted-foreground/60 border-white/5 hover:border-white/15'
                          }`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    data-testid="session-notes-text"
                    value={(() => {
                      const parts = (form.notes || '').split(' — ');
                      return parts.length > 1 ? parts.slice(1).join(' — ') : (
                        ['Stoked!','Epic','Fun','Mellow','Challenging','Frustrating','Exhausting','Progressed','Zen'].includes((form.notes || '').split(' + ')[0]?.trim()) ? '' : form.notes
                      );
                    })()}
                    onChange={e => {
                      const parts = (form.notes || '').split(' — ');
                      const moodPart = parts[0] || '';
                      const moods = moodPart.split(' + ').filter(x => ['Stoked!','Epic','Fun','Mellow','Challenging','Frustrating','Exhausting','Progressed','Zen'].includes(x.trim()));
                      const moodStr = moods.join(' + ');
                      const newNotes = e.target.value ? (moodStr ? `${moodStr} — ${e.target.value}` : e.target.value) : moodStr;
                      setForm({...form, notes: newNotes});
                    }}
                    placeholder="add a note (optional)..."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 resize-none placeholder:text-muted-foreground/40"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground mr-1">Rating:</span>
                  {[1,2,3,4,5].map(r => (
                    <button key={r} type="button" data-testid={`session-rating-${r}`} onClick={() => setForm({...form, rating: r})}>
                      <Star className={`w-4 h-4 ${r <= form.rating ? 'text-secondary fill-secondary' : 'text-muted/50'}`} />
                    </button>
                  ))}
                </div>
                <button data-testid="session-submit-btn" type="submit" className="w-full bg-primary text-primary-foreground text-xs font-bold py-2.5 rounded-xl hover:opacity-90 transition-opacity">
                  Save Session
                </button>
                <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 flex items-center gap-3">
                  <MessageCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[11px] text-muted-foreground leading-snug">Skip the form — just <button type="button" data-testid="session-chat-swell" onClick={() => navigate('/chat')} className="text-primary font-semibold hover:underline">chat with Swell</button> and say "I just surfed" to log a session.</p>
                  </div>
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* List */}
        {sessions.length > 0 ? (
          <div className="space-y-2">
            {sessions.map((s, i) => (
              <motion.div
                key={s.session_id || i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: i * 0.04 }}
                className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4 group"
                data-testid={`session-item-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium">{s.location}</p>
                      <span className="text-[10px] text-muted-foreground">{s.date}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      {s.duration_minutes > 0 && <span>{s.duration_minutes}min</span>}
                      {s.wave_height && <span>{s.wave_height}</span>}
                      {s.board && <span>{s.board}</span>}
                    </div>
                    {s.notes && <p className="text-[11px] text-muted-foreground/70 mt-1.5 italic">"{s.notes}"</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className={`w-2.5 h-2.5 ${j < s.rating ? 'text-secondary fill-secondary' : 'text-muted/50'}`} />
                      ))}
                    </div>
                    <button
                      data-testid={`delete-session-${i}`}
                      onClick={() => handleDelete(s.session_id)}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded-full transition-all"
                    >
                      <Trash2 className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-xs text-muted-foreground">no sessions logged yet</p>
            <p className="text-[10px] text-muted-foreground/60 mt-1">hit the button above to log your first sesh</p>
          </div>
        )}
      </div>
    </PageShell>
  );
}
